// Immutable Agent-version history on the selected Agent hub. The API's
// keyset order is preserved verbatim; the SPA never synthesizes or re-sorts
// history client-side.

import type { ReactNode } from "react";
import { useMemo } from "react";

import type { ColumnDef } from "@tanstack/react-table";

import type { components } from "../../api/schema";
import type { ListQueryResult } from "../../lib/useListQuery";
import { EmptyState, Section } from "../../ui";
import { DateCell } from "../shared/DateCell";
import { NameWithId } from "../shared/NameWithId";
import { PaginatedTableSection } from "../shared/PaginatedTableSection";

type AgentVersion = components["schemas"]["AgentVersionResponse"];

export interface AgentVersionsViewProps {
  detailDrawer?: ReactNode;
  history: ListQueryResult<AgentVersion>;
  onOpenDetail: (versionId: string) => void;
}

export function AgentVersionsView({
  detailDrawer,
  history,
  onOpenDetail,
}: AgentVersionsViewProps) {
  const columns = useMemo<Array<ColumnDef<AgentVersion>>>(
    () => [
      {
        header: "Tag",
        accessorKey: "tag",
        cell: ({ row }) => (
          <NameWithId
            id={row.original.id}
            idLabel="Version ID"
            name={row.original.tag}
            strong
          />
        ),
      },
      {
        header: "Created",
        accessorKey: "created_at",
        cell: ({ row }) => <DateCell value={row.original.created_at} />,
      },
      {
        header: "Created by",
        accessorFn: (version) => version.created_by ?? "System",
      },
    ],
    [],
  );

  return (
    <>
      <Section heading="Version history">
        <PaginatedTableSection
          ariaLabel="Agent version history"
          className="marker-agent-table"
          columns={columns}
          emptyState={
            <EmptyState
              description="Send a version tag with a transcript to create one."
              title="No agent versions"
            />
          }
          errorMessage={history.errorMessage}
          getRowId={(version) => version.id}
          hasNextPage={history.hasNextPage}
          isFetchingNextPage={history.isFetchingNextPage}
          isPending={history.isPending}
          items={history.items}
          listStateKey={history.listStateKey}
          onLoadMore={history.fetchNextPage}
          onRowClick={(row) => {
            onOpenDetail(row.original.id);
          }}
          skeletonLabel="Loading agent versions"
          skeletonRows={3}
        />
      </Section>
      {detailDrawer}
    </>
  );
}
